import React from 'react';
import { useNavigate } from 'react-router-dom';
import { UserProfile } from '../../../types/types';
import { RestUser, UserProfileInput } from '../../../services/auth/types/user';
import { useAppDispatch } from '../../../layouts/App/hooks/useAppSelector';
import { logout, setRestUser, updateRestUser, updateUserProfile } from '../../../store/slices/authSlice';
import { useUser } from '../../../services/auth/hooks/useUser';
import DashboardLayout from '../../layouts/DashboardLayout';
import BirthdateField, { normalizeBirthdate } from '../../components/forms/BirthdateField';
import DeleteAccountModal from './DeleteAccountModal';
import { deleteOwnAccount } from '../../../services/user/api';
import './SettingsPage.css';

interface SettingsPageProps {
  user: UserProfile;
  restUser: RestUser | null;
}

const fallbackAvatar = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128">
  <defs>
    <linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
      <stop stop-color="#0d4f3c"/>
      <stop offset="1" stop-color="#d9ff31"/>
    </linearGradient>
  </defs>
  <rect width="128" height="128" rx="64" fill="url(#g)"/>
  <circle cx="64" cy="48" r="23" fill="#f7efe6"/>
  <path d="M24 116c7-25 22-38 40-38s33 13 40 38" fill="#f7efe6"/>
</svg>
`)}`;

const SettingsPage: React.FC<SettingsPageProps> = ({ user, restUser }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { updateUser } = useUser();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [selectedPicture, setSelectedPicture] = React.useState<string | null>(null);
  const [formData, setFormData] = React.useState({
    firstName: '',
    lastName: '',
    nickname: '',
    birthDate: '',
  });
  const [validationErrors, setValidationErrors] = React.useState<{
    firstName?: string;
    lastName?: string;
  }>({});
  const [isSaving, setIsSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const [saveSuccess, setSaveSuccess] = React.useState(false);
  const [emailNotifications, setEmailNotifications] = React.useState(true);
  const [travelDeals, setTravelDeals] = React.useState(true);
  const [shareTravelUpdates, setShareTravelUpdates] = React.useState(true);
  // Account privacy (D-35): public account → trips/posts default public;
  // closed → followers-only defaults. Per-trip overrides always win.
  const [publicProfile, setPublicProfile] = React.useState(
    () => (restUser as any)?.accountPrivacy !== 'closed',
  );
  const [locationSharing, setLocationSharing] = React.useState(false);
  const [theme, setTheme] = React.useState(() => localStorage.getItem('enroute.theme') || 'light');
  const [language, setLanguage] = React.useState(() => localStorage.getItem('enroute.language') || 'en');
  const [currency, setCurrency] = React.useState(() => localStorage.getItem('enroute.currency') || 'usd');
  const [travelStyle, setTravelStyle] = React.useState(() => localStorage.getItem('enroute.travelStyle') || 'budget');

  const email = restUser?.email || user?.email || 'curdwithraisins@gmail.com';
  const profileName = user?.name && !user.name.includes('@') ? user.name : '';
  const avatar = selectedPicture || restUser?.picture_url || restUser?.avatarUrl || user?.avatar || fallbackAvatar;

  React.useEffect(() => {
    setFormData({
      firstName: restUser?.firstName || profileName.split(' ')[0] || '',
      lastName: restUser?.lastName || '',
      nickname: restUser?.nickname || '',
      birthDate: normalizeBirthdate(restUser?.birthDate),
    });
  }, [restUser, profileName]);

  React.useEffect(() => {
    localStorage.setItem('enroute.theme', theme);
  }, [theme]);

  React.useEffect(() => {
    localStorage.setItem('enroute.language', language);
  }, [language]);

  React.useEffect(() => {
    localStorage.setItem('enroute.currency', currency);
  }, [currency]);

  React.useEffect(() => {
    localStorage.setItem('enroute.travelStyle', travelStyle);
  }, [travelStyle]);

  const clearMessages = () => {
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData((current) => ({
      ...current,
      [field]: value,
    }));
    setValidationErrors((current) => ({
      ...current,
      [field]: undefined,
    }));
    clearMessages();
  };

  const validateForm = () => {
    const errors: { firstName?: string; lastName?: string } = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePhotoSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    clearMessages();

    // Match the server's accepted types (imageValidation.ts) so the user gets an
    // immediate, clear error instead of a failed round-trip.
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setSaveError('Profile photo must be a JPEG, PNG, or WebP image.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setSaveError('Profile photo must be 5MB or smaller.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const imageData = String(reader.result);
      // Local preview only — do NOT push into global state here, or the header
      // and profile page would change before the user saves. The global avatar
      // updates on save (handleSaveChanges) once the server returns the URL.
      setSelectedPicture(imageData);
    };
    reader.onerror = () => {
      setSaveError('Could not read the selected photo. Please try another image.');
    };
    reader.readAsDataURL(file);
  };

  const handleSaveChanges = async () => {
    clearMessages();

    if (!validateForm()) {
      setSaveError('Please fill in the required fields highlighted below.');
      return;
    }

    setIsSaving(true);

    try {
      const updateInput: UserProfileInput = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        accountPrivacy: publicProfile ? 'public' : 'closed',
      };

      if (formData.nickname.trim()) {
        updateInput.nickname = formData.nickname.trim();
      }
      if (formData.birthDate.trim()) {
        updateInput.birthDate = formData.birthDate.trim();
      }
      if (selectedPicture) {
        updateInput.picture = selectedPicture;
      }

      const updatedUser = await updateUser(updateInput);

      if (!updatedUser) {
        throw new Error('Failed to update user data. Please try again.');
      }

      dispatch(restUser ? updateRestUser(updatedUser) : setRestUser(updatedUser));
      dispatch(updateUserProfile({
        name: `${updatedUser.firstName || ''} ${updatedUser.lastName || ''}`.trim() || updatedUser.email,
        email: updatedUser.email,
        avatar: updatedUser.picture_url || updatedUser.avatarUrl || selectedPicture || user?.avatar,
      }));

      setSelectedPicture(null);
      setSaveSuccess(true);
    } catch (error: any) {
      setSaveError(error.message || 'Failed to update user data. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSignOut = () => {
    dispatch(logout());
    navigate('/', { replace: true });
  };

  /**
   * Irreversibly erase the account. The modal owns the confirmation and shows
   * any error; this only runs once the user has typed DELETE.
   *
   * On success the session is already dead server-side (the Cognito user is
   * gone), so we clear local auth state and leave rather than let the app keep
   * rendering with tokens that no longer resolve to anybody. Errors are
   * re-thrown so the modal can surface them and stay open — every failure short
   * of the final step leaves the account intact and the purge retryable.
   */
  const handleDeleteAccount = async () => {
    const { fetchAuthSession } = await import('aws-amplify/auth');
    const session = await fetchAuthSession();
    const accessToken = session.tokens?.accessToken?.toString();
    if (!accessToken) {
      throw new Error('Your session has expired. Please sign in again and retry.');
    }

    await deleteOwnAccount(accessToken);

    setIsDeleteModalOpen(false);
    dispatch(logout());
    navigate('/', { replace: true });
  };

  return (
    <DashboardLayout activeSection="settings">
      <div className="v2-settings-page">
        <section className="v2-settings-card" aria-labelledby="profile-account-heading">
          <h2 id="profile-account-heading">Profile & Account</h2>
          <div className="v2-settings-card__body">
            <div className="v2-settings-photo">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="v2-settings-photo__input"
                onChange={handlePhotoSelected}
              />
              <button
                type="button"
                className="v2-settings-photo__avatar"
                aria-label="Edit profile photo"
                onClick={() => fileInputRef.current?.click()}
              >
                <img
                  src={avatar}
                  alt="Profile avatar"
                  onError={(event) => {
                    event.currentTarget.src = fallbackAvatar;
                  }}
                />
                <span className="v2-settings-photo__edit">Edit</span>
                <span className="v2-settings-photo__badge">+</span>
              </button>
              <div>
                <button type="button" onClick={() => fileInputRef.current?.click()}>Click or drag to upload a new photo</button>
                <p>Max 5MB · JPG, PNG, WebP</p>
              </div>
            </div>

            <div className="v2-settings-form">
              <label className="v2-settings-form__full">
                <FieldLabel label="Email" required />
                <input type="email" value={email} disabled />
              </label>
              <label>
                <FieldLabel label="First Name" required />
                <input
                  type="text"
                  value={formData.firstName}
                  placeholder="Enter your first name"
                  onChange={(event) => handleInputChange('firstName', event.target.value)}
                />
                {validationErrors.firstName && <span className="v2-settings-form__error">{validationErrors.firstName}</span>}
              </label>
              <label>
                <FieldLabel label="Last Name" required />
                <input
                  type="text"
                  value={formData.lastName}
                  placeholder="Enter your last name"
                  onChange={(event) => handleInputChange('lastName', event.target.value)}
                />
                {validationErrors.lastName && <span className="v2-settings-form__error">{validationErrors.lastName}</span>}
              </label>
              <label>
                <FieldLabel label="Nickname" />
                <input
                  type="text"
                  value={formData.nickname}
                  placeholder="Enter your nickname"
                  onChange={(event) => handleInputChange('nickname', event.target.value)}
                />
              </label>
              <label>
                <FieldLabel label="Birthdate" />
                <BirthdateField
                  value={formData.birthDate}
                  onChange={(nextValue) => handleInputChange('birthDate', nextValue)}
                  placeholder="Select your birthdate"
                />
              </label>
            </div>
          </div>
        </section>

        <section className="v2-settings-card" aria-labelledby="preferences-heading">
          <h2 id="preferences-heading">Preferences</h2>
          <div className="v2-settings-card__body">
            <SettingToggle
              title="Email notifications"
              description="Get updates about your trips and account"
              checked={emailNotifications}
              onChange={setEmailNotifications}
            />
            <div className="v2-settings-form v2-settings-form--three">
              <label>
                <FieldLabel label="Theme" />
                <select value={theme} onChange={(event) => setTheme(event.target.value)}>
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="system">System</option>
                </select>
              </label>
              <label>
                <FieldLabel label="Language" />
                <select value={language} onChange={(event) => setLanguage(event.target.value)}>
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                  <option value="it">Italian</option>
                  <option value="pt">Portuguese</option>
                </select>
              </label>
              <label>
                <FieldLabel label="Currency" />
                <select value={currency} onChange={(event) => setCurrency(event.target.value)}>
                  <option value="usd">USD ($)</option>
                  <option value="eur">EUR (€)</option>
                  <option value="gbp">GBP (£)</option>
                  <option value="jpy">JPY (¥)</option>
                  <option value="cad">CAD ($)</option>
                  <option value="aud">AUD ($)</option>
                </select>
              </label>
            </div>
          </div>
        </section>

        <section className="v2-settings-card" aria-labelledby="travel-heading">
          <h2 id="travel-heading">Travel Preferences</h2>
          <div className="v2-settings-card__body">
            <div className="v2-settings-form">
              <label className="v2-settings-form__full">
                <FieldLabel label="Preferred Travel Style" />
                <select value={travelStyle} onChange={(event) => setTravelStyle(event.target.value)}>
                  <option value="budget">Budget Traveler</option>
                  <option value="comfort">Comfort Seeker</option>
                  <option value="luxury">Luxury Explorer</option>
                  <option value="adventure">Adventure Seeker</option>
                  <option value="cultural">Cultural Enthusiast</option>
                </select>
              </label>
            </div>
            <SettingToggle
              title="Travel deals & recommendations"
              description="Get personalized travel deals and suggestions"
              checked={travelDeals}
              onChange={setTravelDeals}
            />
            <SettingToggle
              title="Share travel updates"
              description="Share your trips with friends"
              checked={shareTravelUpdates}
              onChange={setShareTravelUpdates}
            />
          </div>
        </section>

        <section className="v2-settings-card" aria-labelledby="privacy-heading">
          <h2 id="privacy-heading">Privacy & Security</h2>
          <div className="v2-settings-card__body">
            <SettingToggle
              title="Public account"
              description="On: your trips and posts are public by default. Off: they default to followers-only. You can still set any trip's visibility individually."
              checked={publicProfile}
              onChange={setPublicProfile}
            />
            <SettingToggle
              title="Location sharing"
              description="Share your location in posts"
              checked={locationSharing}
              onChange={setLocationSharing}
            />
            <div className="v2-settings-security-actions">
              <button type="button">Change Password</button>
              <button type="button">Two-Factor Auth</button>
              <button type="button">Download My Data</button>
            </div>
          </div>
        </section>

        <div className="v2-settings-actions-card">
          <div className="v2-settings-page__save-group">
            <button
              type="button"
              className="v2-settings-page__primary"
              onClick={handleSaveChanges}
              disabled={isSaving}
            >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M20 6 9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
            {saveSuccess && <span className="v2-settings-page__success">Settings saved</span>}
            {saveError && <span className="v2-settings-page__error">{saveError}</span>}
          </div>
          <div className="v2-settings-page__footer-actions">
            <button type="button" className="v2-settings-page__secondary" onClick={handleSignOut}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Sign Out
            </button>
            <button
              type="button"
              className="v2-settings-page__danger"
              onClick={() => setIsDeleteModalOpen(true)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Delete Account
            </button>
          </div>
        </div>
      </div>

      {isDeleteModalOpen && (
        <DeleteAccountModal
          onCancel={() => setIsDeleteModalOpen(false)}
          onConfirm={handleDeleteAccount}
        />
      )}
    </DashboardLayout>
  );
};

interface FieldLabelProps {
  label: string;
  required?: boolean;
}

const FieldLabel: React.FC<FieldLabelProps> = ({ label, required = false }) => (
  <span className="v2-settings-form__label">
    {label}
    {required && <span aria-hidden="true">*</span>}
  </span>
);

interface SettingToggleProps {
  title: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const SettingToggle: React.FC<SettingToggleProps> = ({ title, description, checked, onChange }) => (
  <div className="v2-settings-toggle">
    <div>
      <h3>{title}</h3>
      <p>{description}</p>
    </div>
    <button
      type="button"
      className={checked ? 'v2-settings-toggle__switch--on' : undefined}
      onClick={() => onChange(!checked)}
      aria-pressed={checked}
      aria-label={title}
    >
      <span />
    </button>
  </div>
);

export default SettingsPage;
